/**
 * Cards Progress Bar Component
 *
 * Shows current position within a set of cards as a horizontal progress indicator.
 * The indicator position and width are calculated based on current/total values.
 * Supports drag interaction and keyboard navigation.
 *
 * @package oska
 */

/**
 * CardsProgress class
 * Manages a progress bar indicator for card navigation
 */
class CardsProgress {
  /**
   * @param {HTMLElement} element - The progress bar element with [data-cards-progress]
   */
  constructor(element) {
    this.container = element;
    this.track = element.querySelector(".js-progress-track");
    this.indicator = element.querySelector(".js-progress-indicator");

    this.current = parseInt(element.dataset.current, 10) || 1;
    this.total = parseInt(element.dataset.total, 10) || 1;

    // Drag state
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartLeft = 0;

    // Scroll activity tracking
    this.scrollTimeout = null;

    this.init();
  }

  /**
   * Initialize the component
   */
  init() {
    this.updateIndicator();
    this.setupDragInteraction();
    this.setupKeyboardNavigation();
    this.setupScrollTracking();
  }

  /**
   * Setup drag interaction on the indicator
   */
  setupDragInteraction() {
    // Make indicator focusable
    this.indicator.setAttribute("tabindex", "0");
    this.indicator.setAttribute("role", "slider");
    this.indicator.setAttribute("aria-valuemin", "1");
    this.indicator.setAttribute("aria-valuemax", this.total);
    this.indicator.setAttribute("aria-valuenow", this.current);
    this.indicator.setAttribute("aria-label", "Card position slider");

    // Mouse events
    this.indicator.addEventListener("mousedown", this.onDragStart.bind(this));
    document.addEventListener("mousemove", this.onDragMove.bind(this));
    document.addEventListener("mouseup", this.onDragEnd.bind(this));

    // Touch events
    this.indicator.addEventListener("touchstart", this.onDragStart.bind(this), {
      passive: false,
    });
    document.addEventListener("touchmove", this.onDragMove.bind(this), {
      passive: false,
    });
    document.addEventListener("touchend", this.onDragEnd.bind(this));

    // Click on track to jump to position
    this.container.addEventListener("click", this.onTrackClick.bind(this));
  }

  /**
   * Setup keyboard navigation
   */
  setupKeyboardNavigation() {
    this.indicator.addEventListener("keydown", (e) => {
      switch (e.key) {
        case "ArrowLeft":
        case "ArrowDown":
          e.preventDefault();
          this.prev();
          break;
        case "ArrowRight":
        case "ArrowUp":
          e.preventDefault();
          this.next();
          break;
        case "Home":
          e.preventDefault();
          this.setCurrent(1);
          break;
        case "End":
          e.preventDefault();
          this.setCurrent(this.total);
          break;
      }
    });
  }

  /**
   * Setup scroll activity tracking for opacity changes
   */
  setupScrollTracking() {
    // Listen for external scroll updates
    this.container.addEventListener("cards-progress:update", (e) => {
      // Sync internal state from ScrollingCards so drag/click use correct values
      if (e.detail) {
        if (typeof e.detail.total === "number" && e.detail.total >= 1) {
          this.total = e.detail.total;
          this.container.dataset.total = this.total;
        }
        if (typeof e.detail.current === "number" && e.detail.current >= 1) {
          this.current = Math.min(e.detail.current, this.total);
          this.container.dataset.current = this.current;
        }
        this.updateAriaValues();
      }
      this.setActive(true);
      this.scheduleDeactivate();
    });
  }

  /**
   * Set active state (for opacity)
   */
  setActive(active) {
    if (active) {
      this.container.classList.add("is-active");
    } else {
      this.container.classList.remove("is-active");
    }
  }

  /**
   * Schedule deactivation after inactivity
   */
  scheduleDeactivate() {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.setActive(false);
    }, 150);
  }

  /**
   * Handle drag start
   */
  onDragStart(e) {
    // Only handle indicator clicks
    if (!e.target.closest(".js-progress-indicator")) return;

    e.preventDefault();
    e.stopPropagation();
    this.isDragging = true;
    this.container.classList.add("is-dragging");

    // Disable transitions immediately
    this.indicator.style.transition = "none";

    const clientX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    this.dragStartX = clientX;
    this.dragStartLeft = parseFloat(this.indicator.style.left) || 0;
  }

  /**
   * Handle drag move
   */
  onDragMove(e) {
    if (!this.isDragging) return;

    e.preventDefault();
    const clientX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - this.dragStartX;
    const trackWidth = this.track.offsetWidth;
    const indicatorWidth = (1 / this.total) * 100;
    const maxLeft = 100 - indicatorWidth;

    // Calculate new percentage position
    const deltaPercent = (deltaX / trackWidth) * 100;
    let newLeft = this.dragStartLeft + deltaPercent;
    newLeft = Math.max(0, Math.min(newLeft, maxLeft));

    // Update indicator position directly
    this.indicator.style.left = `${newLeft}%`;

    // Emit scroll progress for smooth card scrolling
    const scrollProgress = maxLeft > 0 ? newLeft / maxLeft : 0;
    this.emitScrollProgress(scrollProgress);

    // Calculate which card this corresponds to (for ARIA)
    const newCurrent = Math.round(scrollProgress * (this.total - 1)) + 1;

    if (
      newCurrent !== this.current &&
      newCurrent >= 1 &&
      newCurrent <= this.total
    ) {
      this.current = newCurrent;
      this.container.dataset.current = this.current;
      this.updateAriaValues();
    }
  }

  /**
   * Emit scroll progress event for continuous scrolling
   */
  emitScrollProgress(progress, animated = false) {
    const event = new CustomEvent("cards-progress:scroll", {
      bubbles: true,
      detail: {
        progress,
        animated,
        element: this.container,
      },
    });

    this.container.dispatchEvent(event);
  }

  /**
   * Handle drag end
   */
  onDragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.container.classList.remove("is-dragging");

    // Restore default transition
    this.indicator.style.transition = "";

    // Emit final scroll progress based on actual indicator position
    // (avoids snapping back to the nearest discrete card)
    const indicatorWidth = (1 / this.total) * 100;
    const maxLeft = 100 - indicatorWidth;
    const currentLeft = parseFloat(this.indicator.style.left) || 0;
    const scrollProgress = maxLeft > 0 ? currentLeft / maxLeft : 0;
    this.emitScrollProgress(scrollProgress);

    // Update current to match final position
    const newCurrent =
      Math.round(scrollProgress * (this.total - 1)) + 1;
    this.current = Math.max(1, Math.min(newCurrent, this.total));
    this.container.dataset.current = this.current;
    this.updateAriaValues();

    this.scheduleDeactivate();
  }

  /**
   * Handle click on track
   */
  onTrackClick(e) {
    // Don't handle if clicking on indicator (drag handles that)
    if (e.target.closest(".js-progress-indicator")) return;

    const rect = this.track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const indicatorWidth = (1 / this.total) * 100;
    const maxLeft = 100 - indicatorWidth;

    // Center the indicator on the click point
    const clickPercent = (clickX / rect.width) * 100;
    let newLeft = clickPercent - indicatorWidth / 2;
    newLeft = Math.max(0, Math.min(newLeft, maxLeft));

    // Don't set indicator position here — the smooth scroll will trigger
    // handleScroll → updateProgress which moves the indicator in sync.
    const scrollProgress = maxLeft > 0 ? newLeft / maxLeft : 0;
    this.emitScrollProgress(scrollProgress, true);

    // Update current card index
    const newCurrent = Math.round(scrollProgress * (this.total - 1)) + 1;
    this.current = Math.max(1, Math.min(newCurrent, this.total));
    this.container.dataset.current = this.current;
    this.updateAriaValues();

    this.setActive(true);
    this.scheduleDeactivate();
  }

  /**
   * Update indicator position and width
   */
  updateIndicator() {
    if (!this.indicator || this.total <= 0) return;

    // Calculate indicator width (proportional to track)
    const indicatorWidth = (1 / this.total) * 100;

    // Calculate left position - use (total - 1) as divisor to ensure last card reaches end
    const maxPosition = 100 - indicatorWidth;
    const leftPosition =
      this.total > 1
        ? ((this.current - 1) / (this.total - 1)) * maxPosition
        : 0;

    this.indicator.style.width = `${indicatorWidth}%`;
    this.indicator.style.left = `${leftPosition}%`;

    this.updateAriaValues();
  }

  /**
   * Update ARIA attributes
   */
  updateAriaValues() {
    this.container.setAttribute("aria-valuenow", this.current);
    this.container.setAttribute(
      "aria-label",
      `Card ${this.current} of ${this.total}`,
    );
    this.indicator.setAttribute("aria-valuenow", this.current);
    this.indicator.setAttribute("aria-valuemax", this.total);
    this.indicator.setAttribute(
      "aria-valuetext",
      `Card ${this.current} of ${this.total}`,
    );
  }

  /**
   * Set current position
   * @param {number} value - New current position (1-indexed)
   */
  setCurrent(value) {
    const newValue = Math.max(1, Math.min(this.total, value));
    if (newValue !== this.current) {
      this.current = newValue;
      this.container.dataset.current = newValue;
      this.updateIndicator();
      this.emitEvent();
    }
  }

  /**
   * Set total count
   * @param {number} value - New total count
   */
  setTotal(value) {
    this.total = Math.max(1, value);
    this.container.dataset.total = this.total;
    // Clamp current if necessary
    if (this.current > this.total) {
      this.current = this.total;
      this.container.dataset.current = this.current;
    }
    this.updateIndicator();
  }

  /**
   * Move to next position
   * @returns {boolean} Whether position changed
   */
  next() {
    if (this.current < this.total) {
      this.setCurrent(this.current + 1);
      return true;
    }
    return false;
  }

  /**
   * Move to previous position
   * @returns {boolean} Whether position changed
   */
  prev() {
    if (this.current > 1) {
      this.setCurrent(this.current - 1);
      return true;
    }
    return false;
  }

  /**
   * Emit custom event for React integration
   */
  emitEvent() {
    const event = new CustomEvent("cards-progress:change", {
      bubbles: true,
      detail: {
        current: this.current,
        total: this.total,
        element: this.container,
      },
    });

    this.container.dispatchEvent(event);
  }
}

/**
 * Initialize all cards progress bars on the page
 * @returns {CardsProgress[]} Array of initialized CardsProgress instances
 */
export default function initCardsProgress() {
  const progressBars = document.querySelectorAll("[data-cards-progress]");
  const instances = [];

  progressBars.forEach((bar) => {
    instances.push(new CardsProgress(bar));
  });

  // Demo: Interactive controls on styleguide page
  const interactiveProgress = document.getElementById("interactive-progress");
  const prevBtn = document.getElementById("progress-prev");
  const nextBtn = document.getElementById("progress-next");
  const positionDisplay = document.getElementById("progress-position");
  const output = document.getElementById("cards-progress-output");

  if (interactiveProgress && prevBtn && nextBtn) {
    const instance = instances.find((i) => i.container === interactiveProgress);

    if (instance) {
      const updateDisplay = () => {
        if (positionDisplay) {
          positionDisplay.textContent = `Card ${instance.current} of ${instance.total}`;
        }
      };

      prevBtn.addEventListener("click", () => {
        instance.prev();
        updateDisplay();
      });

      nextBtn.addEventListener("click", () => {
        instance.next();
        updateDisplay();
      });
    }
  }

  // Listen for events
  if (output) {
    document.addEventListener("cards-progress:change", (e) => {
      const { current, total } = e.detail;
      output.textContent = `Position changed to ${current} of ${total}`;
    });
  }

  return instances;
}
